import { useSearchParams, useNavigate } from 'react-router-dom'
import { Plus } from 'lucide-react'
import { Button } from '@/shared/components/ui/button'
import { Card } from '@/shared/components/ui/card'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/shared/components/ui/tooltip'
import { QueryErrorScreen } from '@/shared/components/ui/query-error-screen'
import { Pagination } from '@/shared/components/ui/pagination'
import { RefreshButton } from '@/shared/components/ui/refresh-button'
import { AddressCard } from '@/shared/components/address-card'
import { routes, DEFAULT_PAGE_SIZE } from '@/shared/constants'
import { useCountries } from '@/shared/hooks/useCountries'
import { Can } from '@/features/auth'
import { useMyOrganization } from '@/features/organization/hooks/useMyOrganization'
import { OrganizationHeader } from '@/features/organization/components/OrganizationHeader'
import { OrganizationDetailsCard } from '@/features/organization/components/OrganizationDetailsCard'
import { useBusinessesServicesSummary } from '@/features/services'
import { useMyBusinesses } from './hooks/useMyBusinesses'
import { FirstBusinessOnboarding } from './components/FirstBusinessOnboarding'
import { BusinessListItem } from './components/BusinessListItem'
import { BusinessesEmptyState } from './components/BusinessesEmptyState'
import { BusinessesListSkeleton } from './components/BusinessesListSkeleton'
import { BusinessSearchBar } from './components/BusinessSearchBar'

const PAGE_SIZE = DEFAULT_PAGE_SIZE

type ActiveFilter = 'all' | 'active' | 'inactive'

function parseActive(value: string | null): ActiveFilter {
  if (value === 'active' || value === 'inactive') return value
  return 'all'
}

export function BusinessesPage() {
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()

  const search = params.get('q') ?? ''
  const active = parseActive(params.get('active'))
  const page = Math.max(1, Number(params.get('page') ?? '1'))

  const isActive = active === 'all' ? null : active === 'active'

  const { businesses, totalItems, loading, error, refetch } = useMyBusinesses({
    take: PAGE_SIZE,
    skip: (page - 1) * PAGE_SIZE,
    search,
    isActive,
  })
  const { countries } = useCountries()
  const { countByBusinessId, refetch: refetchSummary } = useBusinessesServicesSummary()
  const { organization, loading: orgLoading } = useMyOrganization()
  // Sin org → permitimos crear (abre el wizard de primera vez org+business).
  // Con org + factura pendiente → bloqueamos con tooltip.
  const hasPendingInvoice = !!organization && !organization.canCreateBusiness
  const canCreate = !orgLoading && !hasPendingInvoice

  const updateParams = (next: Record<string, string | null>) => {
    const merged = new URLSearchParams(params)
    for (const [k, v] of Object.entries(next)) {
      if (v === null || v === '') merged.delete(k)
      else merged.set(k, v)
    }
    setParams(merged, { replace: true })
  }

  const handleSearchChange = (value: string) =>
    updateParams({ q: value || null, page: null })
  const handleActiveChange = (value: ActiveFilter) =>
    updateParams({ active: value === 'all' ? null : value, page: null })
  const handlePageChange = (next: number) =>
    updateParams({ page: next > 1 ? String(next) : null })

  const hasFilters = Boolean(search) || active !== 'all'
  const isInitialLoading = loading

  if (error && businesses.length === 0) {
    return <QueryErrorScreen error={error} onRetry={() => refetch()} />
  }

  // Primera vez: sin organización ni negocios. Invita directamente a crear el negocio.
  const firstTime =
    !orgLoading && !isInitialLoading && !organization && businesses.length === 0 && !hasFilters

  if (firstTime) {
    return <FirstBusinessOnboarding />
  }

  return (
    <div className="space-y-8">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-serif text-[36px] font-normal leading-[1.1] tracking-tight text-ink">
            Mis negocios
          </h1>
          <p className="mt-1 text-sm text-ink-muted">
            Tu organización y todos sus negocios en un solo lugar.
          </p>
        </div>
        <RefreshButton
          onClick={async () => {
            await Promise.all([refetch(), refetchSummary()])
          }}
        />
      </header>

      {/* Organización (datos de la cuenta) */}
      {organization && (
        <section className="space-y-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <OrganizationHeader
              organization={organization}
              onEdit={() => navigate(routes.app.organization.detail)}
            />
          </div>
          <div className="grid gap-6 lg:grid-cols-3">
            <div className="lg:col-span-2">
              <OrganizationDetailsCard organization={organization} />
            </div>
            <AddressCard addresses={organization.addresses ?? []} title="Direcciones" />
          </div>
        </section>
      )}

      {/* Negocios */}
      <section className="space-y-4">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-ink">Negocios</h2>
            <p className="mt-0.5 text-sm text-ink-muted">
              Gestiona el estado, los servicios y la facturación de cada negocio.
            </p>
          </div>
          {/* Alta de negocio con organización = OWNER + MANAGER (matriz del
              goal portal-roles-1); el Contable no ve el botón. */}
          <Can permission="businesses:create">
            {canCreate ? (
              <Button variant="primary" onClick={() => navigate(routes.app.businesses.new)}>
                <Plus className="h-4 w-4" />
                Adicionar negocio
              </Button>
            ) : hasPendingInvoice ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span tabIndex={0}>
                    <Button variant="primary" disabled>
                      <Plus className="h-4 w-4" />
                      Adicionar negocio
                    </Button>
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  Paga la factura pendiente para poder crear más negocios.
                </TooltipContent>
              </Tooltip>
            ) : (
              <Button variant="primary" disabled>
                <Plus className="h-4 w-4" />
                Adicionar negocio
              </Button>
            )}
          </Can>
        </div>

        <Card>
          <div className="mb-4">
            <BusinessSearchBar
              search={search}
              isActive={active}
              onSearchChange={handleSearchChange}
              onActiveChange={handleActiveChange}
            />
          </div>

          {isInitialLoading ? (
            <BusinessesListSkeleton />
          ) : businesses.length === 0 ? (
            <BusinessesEmptyState hasFilters={hasFilters} />
          ) : (
            <ul className="divide-y divide-line">
              {businesses.map((b) => (
                <li key={b.id}>
                  <BusinessListItem
                    business={b}
                    countries={countries}
                    activeServicesCount={countByBusinessId.get(b.id) ?? 0}
                  />
                </li>
              ))}
            </ul>
          )}

          {totalItems > PAGE_SIZE && (
            <Pagination
              page={page}
              pageSize={PAGE_SIZE}
              totalItems={totalItems}
              onPageChange={handlePageChange}
            />
          )}
        </Card>
      </section>
    </div>
  )
}
